import React from 'react';
import { MdPayments } from 'react-icons/md';
import {
  FiCreditCard, FiDroplet, FiPhone, FiWifi, FiTv, FiHome, FiShoppingCart,
  FiHeart, FiShield, FiCalendar, FiDollarSign, FiActivity, FiBook, FiTruck,
  FiBriefcase, FiGrid,
} from 'react-icons/fi';
import { BsLightningCharge } from 'react-icons/bs';
import type { ServiceCategory } from '../../types';
import { getCategoryName } from './helpers';

// Icon mapping by code or normalized name (fallback when no iconeUrl)
const ICON_MAP: Record<string, React.ReactNode> = {
  'AGU': <FiDroplet className="text-2xl" />, 'agua': <FiDroplet className="text-2xl" />,
  'ENG': <BsLightningCharge className="text-2xl" />, 'energia': <BsLightningCharge className="text-2xl" />, 'eletricidade': <BsLightningCharge className="text-2xl" />,
  'TEL': <FiPhone className="text-2xl" />, 'telefone': <FiPhone className="text-2xl" />, 'telecomunicacoes': <FiPhone className="text-2xl" />,
  'INT': <FiWifi className="text-2xl" />, 'internet': <FiWifi className="text-2xl" />,
  'RCG': <FiDollarSign className="text-2xl" />, 'recargas': <FiDollarSign className="text-2xl" />,
  'SEG': <FiShield className="text-2xl" />, 'seguro': <FiShield className="text-2xl" />, 'seguros': <FiShield className="text-2xl" />,
  'EVT': <FiCalendar className="text-2xl" />, 'eventos': <FiCalendar className="text-2xl" />,
  'DNT': <FiHeart className="text-2xl" />, 'doacoes': <FiHeart className="text-2xl" />, 'donativos': <FiHeart className="text-2xl" />,
  'FOD': <FiShoppingCart className="text-2xl" />, 'comida': <FiShoppingCart className="text-2xl" />,
  'SHP': <FiShoppingCart className="text-2xl" />, 'shopping': <FiShoppingCart className="text-2xl" />,
  'GOV': <FiBriefcase className="text-2xl" />, 'governo': <FiBriefcase className="text-2xl" />,
  'TEC': <FiGrid className="text-2xl" />, 'tecnologia': <FiGrid className="text-2xl" />,
  'TRP': <FiTruck className="text-2xl" />, 'transporte': <FiTruck className="text-2xl" />,
  'FAR': <FiActivity className="text-2xl" />, 'farmacia': <FiActivity className="text-2xl" />, 'saude': <FiActivity className="text-2xl" />,
  'MKT': <FiShoppingCart className="text-2xl" />, 'mercado': <FiShoppingCart className="text-2xl" />,
  'COT': <MdPayments className="text-2xl" />, 'cotacoes': <MdPayments className="text-2xl" />,
  'CNT': <FiHome className="text-2xl" />, 'contador': <FiHome className="text-2xl" />, 'contadores': <FiHome className="text-2xl" />,
  'tv': <FiTv className="text-2xl" />, 'televisao': <FiTv className="text-2xl" />,
  'educacao': <FiBook className="text-2xl" />, 'habitacao': <FiHome className="text-2xl" />,
  'comercio': <FiShoppingCart className="text-2xl" />, 'negocio': <FiBriefcase className="text-2xl" />,
  'gas': <BsLightningCharge className="text-2xl" />,
  'ecidadao': <FiBriefcase className="text-2xl" />,
  'quotas': <MdPayments className="text-2xl" />,
  'recargacao': <FiDollarSign className="text-2xl" />,
};

const getIcon = (category: ServiceCategory): React.ReactNode => {
  const codigo = (category as any).codigo || '';
  const nome = getCategoryName(category).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
  return ICON_MAP[codigo] || ICON_MAP[nome] || <FiCreditCard className="text-2xl" />;
};

// For backwards compatibility
export const getCategoryStyle = (name: string, index: number) => {
  const normalizedName = name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
  for (const key of Object.keys(ICON_MAP)) {
    if (normalizedName.includes(key)) {
      return { icon: ICON_MAP[key], bg: 'bg-gray-100' };
    }
  }
  return { icon: <FiCreditCard className="text-2xl" />, bg: 'bg-gray-100' };
};

export const CATEGORY_CONFIG = {};
export const DEFAULT_CATEGORY_STYLES = [{ icon: <FiCreditCard className="text-2xl" />, bg: 'bg-gray-100' }];

// Category Icon Component
export interface CategoryItemProps {
  category: ServiceCategory;
  index: number;
  onClick: () => void;
}

const CategoryItem: React.FC<CategoryItemProps> = ({ category, index, onClick }) => {
  const categoryName = getCategoryName(category);
  const cor = (category as any).cor;
  const iconeUrl = (category as any).iconeUrl;

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center p-4 hover:opacity-80 transition-opacity"
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
        style={{ backgroundColor: cor ? `${cor}20` : '#f3f4f6' }}
      >
        {iconeUrl && iconeUrl.startsWith('http') ? (
          <img src={iconeUrl} alt="" className="w-7 h-7 object-contain" onError={(e: any) => { e.target.style.display = 'none'; }} />
        ) : (
          <span style={{ color: cor || '#6b7280' }}>{getIcon(category)}</span>
        )}
      </div>
      <p className="text-sm font-medium text-pagali-gray-700 text-center">
        {categoryName}
      </p>
    </button>
  );
};

export default CategoryItem;
